import React, { useState } from "react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "../../services/toast";
import { Formik, Form, Field } from "formik";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { makeStyles } from 'tss-react/mui';

import { i18n } from "../../translate/i18n";
import logo from "../../assets/logologin.png";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import wallpaper from "../../assets/wallpaper.webp";
import wallpaperVideo from "../../assets/wallpaper.mp4";

const Copyright = () => (
	<Typography
		variant="body2"
		color="textSecondary"
		align="center"
		style={{ fontSize: 12 }}
	>
		{"© "}
		{new Date().getFullYear()}{" "}
		<Link
			color="inherit"
			underline="hover"
			href="https://www.linkedin.com/in/luigy-miranda"
			target="_blank"
			rel="noopener noreferrer"
		>
			ChatEcarga
		</Link>
		{" · Todos los derechos reservados"}
	</Typography>
);

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		position: "relative",
		overflow: "hidden",
		backgroundColor: "#101216",
	},
	video: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
	overlay: {
		position: "absolute",
		inset: 0,
		backgroundColor: "rgba(16, 18, 22, 0.72)",
	},
	card: {
		position: "relative",
		padding: theme.spacing(5, 4, 3),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		width: 400,
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
		borderRadius: 16,
	},
	logo: {
		width: 150,
		marginBottom: theme.spacing(1),
	},
	title: {
		fontSize: 20,
		fontWeight: 700,
	},
	subtitle: {
		fontSize: 13.5,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
		textAlign: "center",
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(3),
	},
	submitButton: {
		margin: theme.spacing(3, 0, 1),
		height: 42,
		fontSize: 14,
	},
	link: {
		marginTop: theme.spacing(1.5),
		fontSize: 13,
	},
	copyright: {
		marginTop: theme.spacing(3),
	},
}));

const UserSchema = Yup.object().shape({
	email: Yup.string()
		.email("Correo electrónico inválido")
		.required("Requerido"),
});

const Forget = () => {
	const { classes } = useStyles();
	const navigate = useNavigate();

	const initialState = { email: "" };

	const [user] = useState(initialState);
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);

	const handleForget = async values => {
		setIsButtonDisabled(true);

		try {
			try {
				const response = await api.post("/validation", values);

				if (!response.data) {
					toastError('Usuario no válido!');
					navigate("/login");
					return;
				}

				toast.success("Se envió un correo con el enlace para restaurar la clave!");
				navigate("/login");

			} catch (error) {
				toastError('Acceso no autorizado');
				navigate("/login");
			}

		} catch (error) {
			setIsButtonDisabled(false);
			toastError(error);
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<video
				className={classes.video}
				autoPlay
				loop
				muted
				playsInline
				poster={wallpaper}
			>
				<source src={wallpaperVideo} type="video/mp4" />
			</video>
			<div className={classes.overlay} />
			<Paper className={classes.card}>
				<img src={logo} alt="Logo" className={classes.logo} />
				<Typography component="h1" className={classes.title}>
					¿Olvidaste tu contraseña?
				</Typography>
				<Typography className={classes.subtitle}>
					Escribe tu correo y te enviamos un enlace para crear una nueva.
				</Typography>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleForget(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors }) => (
						<Form className={classes.form}>
							<Field
								as={TextField}
								variant="outlined"
								fullWidth
								id="email"
								label={i18n.t("signup.form.email")}
								name="email"
								error={touched.email && Boolean(errors.email)}
								helperText={touched.email && errors.email}
								autoComplete="email"
								autoFocus
								required
							/>
							<Button
								type="submit"
								fullWidth
								variant="contained"
								color="primary"
								disabled={isButtonDisabled}
								className={classes.submitButton}
							>
								Enviar enlace
							</Button>
							<Grid container sx={{
								justifyContent: "center"
							}}>
								<Grid>
									<Link
										variant="body2"
										component={RouterLink}
										to="/login"
										className={classes.link}
										underline="hover"
										sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
									>
										Volver al inicio de sesión
									</Link>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
				<Box className={classes.copyright}>
					<Copyright />
				</Box>
			</Paper>
		</div>
	);
};

export default Forget;
